import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import useFixedPosition from "@/hooks/useFixedPosition"
import {
  addDays,
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
} from "date-fns"
import { ChevronLeftIcon, XIcon } from "lucide-react"
import React, { useEffect, useState } from "react"

export const months = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"]

const weekdays = [
  "Domingo",
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
]

interface Props {
  className?: string
  initialDate?: Date
  isUnderModal?: boolean
  useShortIcon?: boolean
  onDateSelected: (date: Date) => void
}

type DateSelectorStep = "year" | "month" | "day"

const DateSelector: React.FC<Props> = ({
  className,
  initialDate,
  isUnderModal = false,
  useShortIcon = false,
  onDateSelected,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [step, setStep] = useState<DateSelectorStep>("year")
  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null)
  const [selectedDay, setSelectedDay] = useState<number | null>(null)

  const popoverRef = React.useRef<HTMLDivElement>(null)
  const triggerRef = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      // Prioritize focusing the currently selected item
      const selectedItem = popoverRef.current?.querySelector('.font-bold.text-primary, .bg-primary') as HTMLElement
      const firstButton = popoverRef.current?.querySelector('button:not([disabled])') as HTMLElement
      const focusTarget = selectedItem || firstButton
      
      focusTarget?.focus()
      
      // Scroll into view if it's a long list (like years)
      if (selectedItem) {
        selectedItem.scrollIntoView({ block: 'center', behavior: 'instant' })
      }
    }
  }, [isOpen, step])

  useEffect(() => {
    if (!initialDate) return
    setSelectedYear(initialDate?.getFullYear())
    setSelectedMonth(initialDate?.getMonth())
    setSelectedDay(initialDate?.getDate())
    setStep("day")
  }, [initialDate])

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 200 }, (_, i) => currentYear - i)

  const handleYearSelect = (year: number) => {
    setSelectedYear(year)
    setStep("month")
  }

  const handleMonthSelect = (monthIndex: number) => {
    setSelectedMonth(monthIndex)
    setStep("day")
  }

  const handleDaySelect = (day: number) => {
    setSelectedDay(day)
    setIsOpen(false)
    onDateSelected(new Date(selectedYear!, selectedMonth!, day))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false)
      return
    }

    if (e.key === "Backspace" && (e.target as HTMLElement).tagName !== "INPUT") {
      if (step === "month") setStep("year")
      else if (step === "day") setStep("month")
      return
    }

    const currentFocus = document.activeElement
    const grid = currentFocus?.closest('.grid, .flex-col')
    if (!grid) return

    const buttons = Array.from(grid.querySelectorAll('button:not([disabled]):not([tabindex="-1"])')) as HTMLButtonElement[]
    const index = buttons.indexOf(currentFocus as HTMLButtonElement)
    if (index === -1) return

    let cols = 1
    if (step === "month") cols = 3
    else if (step === "day") cols = 7

    let nextIndex = -1
    if (e.key === "ArrowRight") nextIndex = index + 1
    else if (e.key === "ArrowLeft") nextIndex = index - 1
    else if (e.key === "ArrowDown") nextIndex = index + cols
    else if (e.key === "ArrowUp") nextIndex = index - cols

    if (nextIndex >= 0 && nextIndex < buttons.length) {
      e.preventDefault()
      buttons[nextIndex].focus()
    }
  }

  const formatDate = () => {
    if (selectedYear && selectedMonth !== null && selectedDay) {
      return `${selectedDay.toString().padStart(2, "0")}/${(selectedMonth + 1)
        .toString()
        .padStart(2, "0")}/${selectedYear}`
    }
    return "DD/MM/YYYY"
  }

  const renderYearSelector = () => (
    <div className="flex flex-col h-64 overflow-y-auto pr-1">
      {years.map(year => (
        <button
          key={year}
          onClick={() => handleYearSelect(year)}
          className={`p-2 cursor-pointer hover:bg-neutral text-sm rounded-md transition-colors ${selectedYear === year ? "bg-primary/10 font-bold text-primary" : ""
            }`}
        >
          {year}
        </button>
      ))}
    </div>
  )

  const renderMonthSelector = () => (
    <div className="grid grid-cols-3 gap-2">
      {months.map((month, index) => (
        <button
          key={month}
          onClick={() => handleMonthSelect(index)}
          className={`p-2 cursor-pointer hover:bg-neutral text-xs rounded-md transition-colors ${selectedMonth === index ? "bg-primary/10 font-bold text-primary" : ""
            }`}
        >
          {month}
        </button>
      ))}
    </div>
  )

  const allDaysInMonth = (firstDayOfMonth: Date) => {
    let start = startOfWeek(firstDayOfMonth, { weekStartsOn: 0 })
    let end = endOfWeek(endOfMonth(firstDayOfMonth), { weekStartsOn: 0 })
    let days = []
    let day = start

    while (day <= end) {
      days.push(new Date(day))
      day = addDays(day, 1)
    }
    return days
  }

  const renderDaySelector = () => {
    if (!selectedYear || selectedMonth === null) return null
    const firstDayOfMonth = startOfMonth(
      new Date(selectedYear, selectedMonth + 1, 0),
    )
    const days = allDaysInMonth(firstDayOfMonth)

    return (
      <div className="grid grid-cols-7 gap-2">
        {weekdays.map(day => (
          <div key={day} className="text-center font-bold text-sm">
            {day && day.substring(0, 2)}
          </div>
        ))}
        {days.map((day, idx) => (
          <React.Fragment key={idx}>
            {day.getMonth() === firstDayOfMonth.getMonth() && (
              <Button
                key={idx}
                variant={selectedDay === day.getDate() ? "default" : "neutral"}
                onClick={() => handleDaySelect(day.getDate())}
                className={`py-1 text-xs`}
              >
                {day.getDate()}
              </Button>
            )}
            {day.getMonth() !== firstDayOfMonth.getMonth() && (
              <Button
                key={idx}
                variant="ghost"
                tabIndex={-1}
                style={{ cursor: 'default' }}
                className={`py-1 text-xs opacity-30 pointer-events-none`}
                onClick={e => e.preventDefault()}
              >
                {day.getDate()}
              </Button>
            )}
          </React.Fragment>
        ))}
      </div>
    )
  }

  const { top, left, baseRef } = useFixedPosition({ targetId: "date-selector", isUnderModal })

  return (
    <div className={`relative ${className}`} ref={baseRef}>
      {!useShortIcon && (
        <Input
          id={"date"}
          type="text"
          readOnly
          placeholder="DD/MM/YYYY"
          className="cursor-pointer focus:ring-2 focus:ring-primary/20"
          value={formatDate() === "DD/MM/YYYY" ? "" : formatDate()}
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen(true)
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              e.stopPropagation()
              setIsOpen(true)
            }
          }}
        />
      )}
      {useShortIcon && (
        <Button
          title={"Cambiar fecha"}
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen(true)
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              e.stopPropagation()
              setIsOpen(true)
            }
          }}
          className="w-min bg-white dark:bg-white hover:bg-primary group"
        />
      )}
      {isOpen && (
        <div
          ref={popoverRef}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
          className="fixed border rounded-xl shadow-lg p-4 w-[18rem] bg-white z-30 outline-none"
          id="date-selector"
          style={{ top, left }}
        >
          <div className="flex justify-between items-center mb-4">
            <button
              className={`${step === "year" ? "invisible" : "cursor-pointer"
                } p-1 hover:bg-neutral rounded-full transition-colors`}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setStep(step === "month" ? "year" : "month")
              }}
              title="Volver"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <span className="font-bold">
              {step === "year" && "Año"}
              {step === "month" && selectedYear}
              {step === "day" &&
                `${months[selectedMonth || 0]} ${selectedYear}`}
            </span>
            <button
              className="p-1 hover:bg-neutral rounded-full transition-colors"
              onClick={() => setIsOpen(false)}
              title="Cerrar"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>
          {step === "year" && renderYearSelector()}
          {step === "month" && renderMonthSelector()}
          {step === "day" && renderDaySelector()}
        </div>
      )}
    </div>
  )
}

export default DateSelector
